import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn';

/*
  Textarea — многострочное поле: ответ в ветке, заметка, причина блокировки. Вид — как у Input
  (те же рамки, кольцо фокуса, ошибка под полем), высота растёт по строкам до rows.
*/

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helper?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, helper, error, className, id, rows = 3, ...rest }, ref) => {
    const reactId = useId();
    const fieldId = id ?? `textarea-${reactId}`;
    const helpId = `${fieldId}-help`;
    return (
      <div data-slot="field" className="flex w-full flex-col gap-1.5">
        {label && (
          <label htmlFor={fieldId} className="text-xs font-medium text-fg-muted">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows}
          data-slot="textarea"
          className={cn(
            'w-full resize-none rounded-md border bg-card px-3 py-2 text-sm text-fg placeholder:text-fg-subtle',
            'transition-colors duration-fast focus-visible:ring-2 focus-visible:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error
              ? 'border-danger focus-visible:border-danger focus-visible:ring-danger/40'
              : 'border-line hover:border-line-strong focus-visible:border-ring focus-visible:ring-ring/40',
            className,
          )}
          aria-invalid={!!error}
          aria-describedby={helper || error ? helpId : undefined}
          {...rest}
        />
        {(helper || error) && (
          <p id={helpId} className={cn('text-xs', error ? 'text-danger-fg' : 'text-fg-subtle')}>
            {error || helper}
          </p>
        )}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';
